import { useEffect } from "react";
import { RefreshCw, CircleCheck, CircleX, FileText, Newspaper, ListTree, Rocket } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useSite } from "@/context/SiteContext";

const timeAgo = (iso) => {
  if (!iso) return "—";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="flex flex-col items-center gap-0.5 rounded-[10px] bg-black/[0.04] border border-black/10 py-2">
    <Icon size={13} strokeWidth={1.75} className="text-[#0050c0]" />
    <span className="text-[15px] font-extrabold text-[#383838] font-nunito leading-none">{value ?? "—"}</span>
    <span className="text-[8.5px] uppercase tracking-[0.14em] text-[#8a8a8a] font-nunito font-bold">{label}</span>
  </div>
);

/** Live per-site scorecard shown inside the site switcher (light theme). */
export const SiteHealthCard = ({ siteId }) => {
  const { healthMap, healthLoading, fetchHealth } = useSite();
  const health = healthMap[siteId];
  const loading = healthLoading[siteId];

  useEffect(() => {
    if (siteId) fetchHealth(siteId);
  }, [siteId, fetchHealth]);

  if (!siteId) return null;

  if (loading && !health) {
    return (
      <div data-testid="site-health-card-loading" className="mt-2 space-y-2">
        <Skeleton className="h-4 w-2/3 bg-black/10" />
        <div className="grid grid-cols-3 gap-2">
          <Skeleton className="h-14 bg-black/10" />
          <Skeleton className="h-14 bg-black/10" />
          <Skeleton className="h-14 bg-black/10" />
        </div>
        <Skeleton className="h-4 w-1/2 bg-black/10" />
      </div>
    );
  }

  if (!health || health.error) {
    return (
      <div data-testid="site-health-card-error" className="mt-2 text-[11px] text-[#8a8a8a]">
        Could not load health.
        <button type="button" data-testid="site-health-retry-button" onClick={() => fetchHealth(siteId, true)} className="ml-2 text-[#0050c0] hover:underline">Retry</button>
      </div>
    );
  }

  const ok = health.connection?.ok;
  const deployFailed = health.last_deploy?.status === "failed";
  return (
    <div data-testid="site-health-card" className={`mt-2 rounded-[12px] ${!ok || deployFailed ? "ring-1 ring-[#f07000]/40 bg-[#fef2e5]/60 p-2" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5" data-testid="health-connection-status">
          {ok ? <CircleCheck size={13} className="text-[#0050c0]" /> : <CircleX size={13} className="text-[#c25a00]" />}
          <span className={`font-nunito text-[9px] font-extrabold uppercase tracking-[0.14em] ${ok ? "text-[#0050c0]" : "text-[#c25a00]"}`}>
            {ok ? `Connected · ${health.connection.latency_ms}ms` : "Connection failed"}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {health.flatsome && (
            <Badge data-testid="health-flatsome-badge" className="bg-black/[0.06] text-[#6e6e6e] border border-black/10 rounded-[6px] px-1.5 py-0 text-[8.5px] font-nunito font-extrabold uppercase tracking-[0.1em] hover:bg-black/[0.06]">
              Flatsome
            </Badge>
          )}
          <button
            type="button"
            aria-label="Refresh health"
            data-testid="site-health-refresh-button"
            onClick={() => fetchHealth(siteId, true)}
            className="text-[#9a9a9a] hover:text-[#383838] transition-colors duration-150"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      <div className="mt-2 grid grid-cols-3 gap-2">
        <Stat icon={FileText} label="Pages" value={health.pages} testid="health-pages-count" />
        <Stat icon={Newspaper} label="Posts" value={health.posts} testid="health-posts-count" />
        <Stat icon={ListTree} label="Menus" value={health.menus} testid="health-menus-count" />
      </div>

      <div data-testid="health-last-deploy" className="mt-2 flex items-center gap-1.5 text-[10px] text-[#6e6e6e]">
        <Rocket size={11} className="text-[#0050c0] shrink-0" />
        {health.last_deploy ? (
          <span className="truncate">
            Last deploy: <b className="text-[#383838]">{health.last_deploy.target}</b> · {health.last_deploy.mode} ·{" "}
            <span className={health.last_deploy.status === "completed" ? "text-[#0050c0]" : health.last_deploy.status === "failed" ? "text-[#c25a00] font-bold" : "text-[#b45309]"}>{health.last_deploy.status}</span> · {timeAgo(health.last_deploy.created_at)}
          </span>
        ) : (
          <span>No deploys yet</span>
        )}
      </div>
    </div>
  );
};
